import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { IFilterInvisible } from 'core-components/sitebuilder/layout/flexGridWrapper/models/IFilterInvisible'
import { FlexDirectionBreak } from 'core-components/sitebuilder/models/flexGrid/FlexDirectionBreak'
import { IFlexItem } from 'core-components/sitebuilder/models/flexGrid/IFlexItem'
import { IFlexLayout } from 'core-components/sitebuilder/models/flexGrid/IFlexLayout'
import { IVisibilityWidgetSettings } from 'core-components/sitebuilder/models/flexGrid/ISystemWidgetSettings'
import { selectRouteParams } from 'core-components/sitebuilder/store/selectors/selectRouteParams'

interface IUseVisibleSettings {
  settings: IFlexLayout
}

export const useVisibleSettings = (props: IUseVisibleSettings) => {
  const routeParams = useSelector(selectRouteParams)
  const [pinOutedWidgets, setPinOutedWidgets] = useState<object>({})

  const [visibleSettings, setVisibleSettings] = useState<IFilterInvisible>({
    settings: { items: [] as IFlexItem[] },
    hidden: { items: [] as IFlexItem[] },
  })

  const checkVisibleSettings = useCallback(
    ({ showWhenValue, showWhen, hideWhenValue, hideWhen }: IVisibilityWidgetSettings = {}) => {
      const calcHideByHideParam = () => {
        if (!hideWhen) {
          return false
        }

        if (hideWhenValue) {
          return routeParams[hideWhen] === hideWhenValue
        }

        // TODO: Change this structure - remove hideWhen, showWhen + values params and use visibilitySettings: JSON
        const params = hideWhen.split(',')
        return params.some((param) => Boolean(routeParams[param]))
      }

      const hideByShowParam =
        showWhen &&
        (showWhenValue ? routeParams[showWhen] !== showWhenValue : !routeParams[showWhen])

      return { hideByShowParam, hideByHideParam: calcHideByHideParam() }
    },
    [routeParams]
  )

  const filterInvisible = useCallback(
    (data: IFlexLayout): IFilterInvisible => {
      const settingsCopy = structuredClone(data) as IFlexLayout
      const hidden = {
        items: [],
      } as IFlexLayout
      settingsCopy.items = settingsCopy.items?.filter((current) => {
        if (current.isFlexLayout && current.layoutSet) {
          const checkWrapperData = checkVisibleSettings(current.visibilitySettings || {})
          if (checkWrapperData.hideByShowParam || checkWrapperData.hideByHideParam) {
            return false
          }

          const layoutSet = filterInvisible(current.layoutSet)
          if (layoutSet) {
            current.layoutSet = layoutSet.settings
            layoutSet.hidden.items && layoutSet.hidden.items.forEach((el) => hidden.items.push(el))
            return true
          }
          return false
        }

        let hidePinOut = false
        if (current) {
          for (const obj of Object.values(pinOutedWidgets)) {
            if (obj.hidePinOut === current.name) {
              hidePinOut = true
            }
          }
        }

        const { hideByHideParam, hideByShowParam } = checkVisibleSettings(
          current?.systemWidgetSettings
        )

        if (hideByShowParam || hideByHideParam) {
          return false
        }

        if (hidePinOut) {
          hidden.items.push(current)
          return false
        }

        return true
      })

      return { settings: settingsCopy, hidden }
    },
    [routeParams, pinOutedWidgets]
  )

  const newVisibleSettings = filterInvisible(props.settings)

  if (newVisibleSettings.hidden.items?.length) {
    newVisibleSettings.settings.items = [
      ...newVisibleSettings.settings.items,
      {
        widgets: [],
        gap: 0,
        margin: 0,
        isFlexLayout: true,
        canWrap: true,
        size: FlexDirectionBreak.p1212,
        name: 'hidden-cells',
        customWidgetSettings: {},
        systemWidgetSettings: {},
        layoutSet: {
          items: [...newVisibleSettings.hidden.items],
        },
      },
    ]
  }

  if (JSON.stringify(newVisibleSettings) !== JSON.stringify(visibleSettings)) {
    setVisibleSettings(newVisibleSettings)
  }

  useEffect(() => {
    const readPinOutedFromStore = () => {
      const pinouted = localStorage.getItem('pin_out_widgets')
      const pinOutedObj = JSON.parse(pinouted || '{}')
      if (JSON.stringify(pinOutedObj) !== JSON.stringify(pinOutedWidgets)) {
        setPinOutedWidgets({ ...pinOutedObj })
      }
    }
    readPinOutedFromStore()
  }, [pinOutedWidgets])

  return { visibleSettings }
}
